import React, { useState, useRef, useEffect } from 'react';
import {Tag, Plus, Edit3, FileText, RotateCcw, X, MoreHorizontal} from 'lucide-react';
import { toast } from 'sonner';
import {
    checkOverlap,
    computeSelectionOffsets,
    renderAnnotatedText as renderAnnotatedTextUtil,
} from './annotatableTextUtils/index';

interface Category {
    id: number;
    name: string;
    color: string;
    textColor: string;
}

interface Annotation {
    id: number;
    start: number;
    end: number;
    text: string;
    categoryId: number;
}

interface CategoryItem {
    id: number;
    text: string;
}

interface SelectedText {
    text: string;
    start: number;
    end: number;
    rect: { x: number; y: number };
}

interface TooltipMenu {
    x: number;
    y: number;
}

const ThoughtOrganizer: React.FC<any>= ({
                                       config: {
                                           categories = [],
                                           showCategories = true,
                                           showReorder = true,
                                           className = "",
                                       } = {},
                                       initialText ='',
                                       userData,
                                       content,
                                       onChange,
                                       readonly = false
                                   }) => {
    const [text, setText] = useState<string>(initialText);
    const [combinedCategories, setCombinedCategories] = useState<Category[]>(userData?.categories || [
        ...categories
    ]);
    const [annotations, setAnnotations] = useState<Annotation[]>(userData?.annotations || []);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [view, setView] = useState<'annotate' | 'summary'>('annotate');
    const [newCategoryName, setNewCategoryName] = useState<string>('');
    const [showAddCategory, setShowAddCategory] = useState<boolean>(false);
    const [categoryNotes, setCategoryNotes] = useState<Record<number, string>>({});
    const [categoryItems, setCategoryItems] = useState<Record<number, CategoryItem[]>>(userData?.categoryItems || {});
    const [newItemText, setNewItemText] = useState<Record<number, string>>({});
    const [tooltipMenu, setTooltipMenu] = useState<TooltipMenu | null>(null);
    const [selectedText, setSelectedText] = useState<SelectedText | null>(null);
    const textRef = useRef<HTMLDivElement | null>(null);

    // Track last local change to distinguish local vs remote updates
    const lastLocalDataRef = useRef<string>("");
    const isExternalUpdateRef = useRef(false);

    // Sync from external data (e.g., real-time meeting updates)
    useEffect(() => {
        if (!userData || typeof userData !== 'object') return;
        const ext = userData as Record<string, any>;
        if (!ext.text && !ext.annotations && !ext.categoryItems) return;

        const externalStr = JSON.stringify({ text: ext.text, annotations: ext.annotations, categoryItems: ext.categoryItems });
        if (externalStr === lastLocalDataRef.current) return;

        isExternalUpdateRef.current = true;
        if (ext.text !== undefined) setText(ext.text);
        if (ext.annotations) setAnnotations(ext.annotations);
        if (ext.categories) setCombinedCategories(ext.categories);
        if (ext.categoryNotes) setCategoryNotes(ext.categoryNotes);
        if (ext.categoryItems) setCategoryItems(ext.categoryItems);
    }, [userData]);

    const pastelColors = [
        { bg: 'bg-pink-200', text: 'text-pink-800' },
        { bg: 'bg-purple-200', text: 'text-purple-800' },
        { bg: 'bg-primary/20', text: 'text-primary' },
        { bg: 'bg-green-200', text: 'text-green-800' },
        { bg: 'bg-yellow-200', text: 'text-yellow-800' },
        { bg: 'bg-indigo-200', text: 'text-indigo-800' },
        { bg: 'bg-teal-200', text: 'text-teal-800' },
        { bg: 'bg-rose-200', text: 'text-rose-800' },
    ];

    useEffect(() => {
        if (isExternalUpdateRef.current) {
            isExternalUpdateRef.current = false;
            lastLocalDataRef.current = JSON.stringify({ text, annotations, categoryItems });
            return;
        }
        if (!readonly) {
            lastLocalDataRef.current = JSON.stringify({ text, annotations, categoryItems });
            onChange(content?.key ?? "thought-organizer", {
                text,
                annotations,
                categories: combinedCategories,
                categoryNotes,
                categoryItems,
            });
        }
    }, [text, annotations, combinedCategories, categoryNotes, categoryItems]);

    const handleTextSelection = () => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const selectedTextContent = selection.toString().trim();

        if (selectedTextContent.length === 0) {
            setTooltipMenu(null);
            setSelectedText(null);
            return;
        }

        const rect = range.getBoundingClientRect();
        const textNode = textRef.current;
        const textContent = textNode?.textContent ?? '';
        const offsets = computeSelectionOffsets(textContent, selectedTextContent, range.startOffset);

        if (!offsets) {
            setTooltipMenu(null);
            setSelectedText(null);
            return;
        }

        const { start, end } = offsets;

        if (checkOverlap(annotations, start, end)) {
            toast.error('Selection overlaps an existing annotation — please select different text.');
            selection.removeAllRanges();
            setTooltipMenu(null);
            setSelectedText(null);
            return;
        }

        setSelectedText({
            text: selectedTextContent,
            start,
            end,
            rect: {
                x: rect.left + window.scrollX,
                y: rect.top + window.scrollY - 10
            }
        });

        const rawX = rect.left + rect.width / 2;
        const rawY = rect.top - 10;
        setTooltipMenu({
            x: Math.min(Math.max(rawX, 120), window.innerWidth - 120),
            y: rawY
        });
    };

    const annotateWithCategory = (categoryId: number) => {
        if (!selectedText) return;

        const newAnnotation: Annotation = {
            id: Date.now(),
            start: selectedText.start,
            end: selectedText.end,
            text: selectedText.text,
            categoryId: categoryId
        };

        setAnnotations(prev => [...prev, newAnnotation].sort((a, b) => a.start - b.start));

        // Clear selection and tooltip
        window.getSelection()?.removeAllRanges();
        setTooltipMenu(null);
        setSelectedText(null);
    };

    const addCategory = () => {
        if (!newCategoryName.trim()) return;

        const colorIndex = categories.length % pastelColors.length;
        const newCategory: Category = {
            id: Date.now(),
            name: newCategoryName.trim(),
            color: pastelColors[colorIndex].bg,
            textColor: pastelColors[colorIndex].text
        };

        setCombinedCategories(prev => [...prev, newCategory]);
        setNewCategoryName('');
        setShowAddCategory(false);
    };

    const removeAnnotation = (annotationId: number) => {
        setAnnotations(prev => prev.filter(a => a.id !== annotationId));
    };

    const renderAnnotatedText = () => {
        return renderAnnotatedTextUtil(
            text,
            annotations,
            (annotation: Annotation) => {
                const category = combinedCategories.find(c => c.id === annotation.categoryId);
                return {
                    color: category?.color ?? '',
                    textColor: category?.textColor ?? '',
                    label: category?.name ?? 'Category',
                };
            },
            removeAnnotation,
            readonly
        );
    };

    const getCategoryAnnotations = (categoryId: number) => {
        return annotations.filter(a => a.categoryId === categoryId);
    };

    const updateCategoryNote = (categoryId: number, note: string) => {
        setCategoryNotes(prev => ({
            ...prev,
            [categoryId]: note
        }));
    };

    const addCategoryItem = (categoryId: number) => {
        const itemText = newItemText[categoryId];
        if (!itemText || !itemText.trim()) return;

        setCategoryItems(prev => ({
            ...prev,
            [categoryId]: [...(prev[categoryId] || []), {
                id: Date.now(),
                text: itemText.trim()
            }]
        }));

        setNewItemText(prev => ({
            ...prev,
            [categoryId]: ''
        }));
    };

    const removeCategoryItem = (categoryId: number, itemId: number) => {
        setCategoryItems(prev => ({
            ...prev,
            [categoryId]: prev[categoryId]?.filter(item => item.id !== itemId) || []
        }));
    };

    const updateNewItemText = (categoryId: number, text: string) => {
        setNewItemText(prev => ({
            ...prev,
            [categoryId]: text
        }));
    };

    const resetAll = () => {
        setText('');
        setAnnotations([]);
        setCategoryNotes({});
        setCategoryItems({});
        setNewItemText({});
        setView('annotate');
        setTooltipMenu(null);
        setSelectedText(null);
    };

    // Close tooltip when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (tooltipMenu && !(event.target as HTMLElement).closest('.tooltip-menu')) {
                setTooltipMenu(null);
                setSelectedText(null);
                window.getSelection()?.removeAllRanges();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [tooltipMenu]);

    return (
        <>
                {/* View Toggle */}
    {/*            <div className="flex gap-2 mb-6">
                    <button
                        onClick={() => setView('annotate')}
                        className={`px-4 py-2 rounded-lg transition-all ${
                            view === 'annotate'
                                ? 'bg-primary text-white shadow-lg'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        <FileText className="w-4 h-4 inline mr-2" />
                        Annotate Text
                    </button>
                    <button
                        onClick={() => setView('summary')}
                        className={`px-4 py-2 rounded-lg transition-all ${
                            view === 'summary'
                                ? 'bg-purple-500 text-white shadow-lg'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        <Tag className="w-4 h-4 inline mr-2" />
                        Category Summary
                    </button>
                    <button
                        onClick={resetAll}
                        className="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all ml-auto"
                    >
                        <RotateCcw className="w-4 h-4 inline mr-2" />
                        Reset
                    </button>
                </div>*/}

                {view === 'annotate' && (
                    <>
                    <div className="space-y-6 p-6 rounded-2xl bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 border-2 border-gray-200 shadow-lg mt-5">
                        {/* Text Input */}
                        {!initialText && !readonly &&
                        <div>
                            <label className="block text-left text-base font-semibold text-gray-800 mb-3 bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                                Your thoughts (write as much as you need):
                            </label>
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="Write your thoughts here... Express everything that's on your mind - your feelings, challenges, dreams, what matters to you, and how you might approach things."
                                className="w-full h-40 p-4 border-2 border-primary/30 rounded-xl focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all resize-none bg-white shadow-sm"
                            />
                        </div>
                        }


                        {/* Tooltip Menu for Text Selection */}
                        {tooltipMenu && (
                            <div
                                className="tooltip-menu fixed z-50 bg-white rounded-xl shadow-2xl border-2 border-primary/30 p-3"
                                style={{
                                    left: `${tooltipMenu.x}px`,
                                    top: `${tooltipMenu.y}px`,
                                    transform: 'translate(-50%, -100%)'
                                }}
                            >
                                <div className="text-xs font-semibold text-gray-700 mb-2 px-2">Categorize as:</div>
                                <div className="flex flex-wrap gap-2 max-w-64">
                                    {combinedCategories.map(category => (
                                        <button
                                            key={category.id}
                                            onClick={() => annotateWithCategory(category.id)}
                                            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all hover:scale-105 shadow-sm ${
                                                category.color
                                            } ${category.textColor} hover:shadow-md`}
                                        >
                                            {category.name}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    onClick={() => {
                                        setTooltipMenu(null);
                                        setSelectedText(null);
                                        window.getSelection()?.removeAllRanges();
                                    }}
                                    className="absolute -top-2 -right-2 w-7 h-7 bg-gray-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-gray-600 shadow-md transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        {/* Annotated Text Display */}
                        { text  && (
                            <div className={readonly ? 'disabled pointer-events-none  select-none': ''}>
                                <label className="block text-left text-base font-semibold text-gray-800 mb-3">
                                    Your annotated text:
                                </label>
                                <div
                                    ref={textRef}
                                    onMouseUp={handleTextSelection}
                                    className="p-5 border-2 border-primary/30 rounded-xl bg-white min-h-32 leading-relaxed cursor-text select-text shadow-sm hover:shadow-md transition-shadow"
                                    style={{ userSelect: 'text' }}
                                >
                                    {renderAnnotatedText() || text}
                                </div>
                            </div>
                        )}

                        {/* Categories */}
                        {!readonly &&
                        <div>
                            <div className="flex flex-wrap gap-2 mb-4">
                                {combinedCategories.map(category => (
                                    <button
                                        key={category.id}
                                        className={`px-4 py-2 text-xs font-medium rounded-full transition-all transform shadow-sm ${
                                            category.color
                                        } ${category.textColor} ${
                                            selectedCategory?.id === category.id
                                                ? 'ring-4 ring-primary/30 shadow-lg scale-105'
                                                : 'hover:scale-105'
                                        }`}
                                    >
                                        {category.name}
                                    </button>
                                ))}
                                <button
                                    onClick={() => setShowAddCategory(true)}
                                    className="p-2 rounded-full bg-gradient-to-r from-primary to-indigo-500 text-white hover:from-primary hover:to-indigo-600 transition-all shadow-md hover:shadow-lg"
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                            </div>

                            {showAddCategory && (
                                <div className="flex gap-2 mb-4 text-xs">
                                    <input
                                        type="text"
                                        value={newCategoryName}
                                        onChange={(e) => setNewCategoryName(e.target.value)}
                                        placeholder="New category name"
                                        className="px-4 py-2 border-2 border-primary/30 rounded-xl focus:border-primary focus:ring-4 focus:ring-primary/10 shadow-sm"
                                        onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                                    />
                                    <button
                                        onClick={addCategory}
                                        className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all shadow-md"
                                    >
                                        Add
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowAddCategory(false);
                                            setNewCategoryName('');
                                        }}
                                        className="px-4 py-2 bg-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-400 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}

                            {selectedCategory && (
                                <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4">
                                    <p className="text-primary text-sm">
                                        <strong>{selectedCategory.name}</strong> category selected.
                                        Highlight any text in your writing below to categorize it.
                                    </p>
                                </div>
                            )}
                        </div>
                        }
                    </div>



                    {/* Trello-style board layout */}
                    <div className={'flex gap-6 overflow-x-auto pb-4 text-xs '}>
                        {combinedCategories.map(category => {
                            const categoryAnnotations = getCategoryAnnotations(category.id);
                            const categoryItemsList = categoryItems[category.id] || [];
                            const allCards = [
                                ...categoryAnnotations.map(ann => ({
                                    id: `annotation-${ann.id}`,
                                    type: 'annotation',
                                    content: ann.text,
                                    source: 'From your text',
                                    originalId: ann.id
                                })),
                                ...categoryItemsList.map(item => ({
                                    id: `item-${item.id}`,
                                    type: 'item',
                                    content: item.text,
                                    source: 'Added manually',
                                    originalId: item.id
                                }))
                            ];

                            return (
                                <div
                                    key={category.id}
                                    className={'flex-shrink-0 w-[220px] bg-gradient-to-br from-white to-gray-50 rounded-2xl p-4 border-2 border-gray-200 shadow-md hover:shadow-lg transition-all '+ (readonly ? 'disabled pointer-events-none  select-none mt-3': '')}
                                >
                                    {/* Board Header */}
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                        <span className={`px-4 py-2 rounded-xl text-sm font-semibold shadow-sm ${category.color} ${category.textColor}`}>
                          {category.name}
                        </span>
                                            <span className="text-xs font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-full shadow-sm">
                          {allCards.length}
                        </span>
                                        </div>
                                        <button className="p-1 hover:bg-gray-200 rounded transition-colors">
                                            <MoreHorizontal className="w-4 h-4 text-gray-500" />
                                        </button>
                                    </div>

                                    {/* Cards Container */}
                                    <div className="space-y-3 min-h-32">
                                        {allCards.map(card => (
                                            <div
                                                key={card.id}
                                                className="bg-white rounded-xl p-3 shadow-sm hover:shadow-md transition-all cursor-pointer border-2 border-gray-200 hover:border-primary/30 group"
                                            >
                                                <div className="flex justify-between items-start ">
                                                        <p className="text-sm text-gray-800 leading-relaxed">
                                                            {card.content}
                                                        </p>
                                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button
                                                                    onClick={() => {
                                                                        if (card.type === 'annotation') {
                                                                            removeAnnotation(card.originalId);
                                                                        } else {
                                                                            removeCategoryItem(category.id, card.originalId);
                                                                        }
                                                                    }}
                                                                    className="p-1 hover:bg-red-100 rounded text-gray-400 hover:text-red-500 transition-colors"
                                                                    title="Delete card"
                                                                >
                                                                    <X className="w-3 h-3" />
                                                                </button>
                                                            </div>
                                                </div>
                                            </div>
                                        ))}

                                        {allCards.length === 0 && (
                                            <div className="text-center py-8 text-gray-500">
                                                <div className="text-sm mb-2">No cards yet</div>
                                                <div className="text-xs">Highlight text or add items below</div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Add New Card */}
                                    {!readonly &&
                                    <div className="space-y-3  mt-4">
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={newItemText[category.id] || ''}
                                                onChange={(e) => updateNewItemText(category.id, e.target.value)}
                                                placeholder="Add a card..."
                                                className="flex-1 px-3 py-2 w-[100px] text-sm border border-gray-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white"
                                                onKeyDown={(e) => e.key === 'Enter' && addCategoryItem(category.id)}
                                            />
                                            <button
                                                onClick={() => addCategoryItem(category.id)}
                                                className="min-h-[40px] min-w-[40px] px-3 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition-all active:scale-[0.97] flex items-center"
                                            >
                                                <Plus className="w-4 h-4" />
                                            </button>
                                        </div>

                                        {/* Board Notes - Always visible */}
                                        <textarea
                                            value={categoryNotes[category.id] || ''}
                                            onChange={(e) => updateCategoryNote(category.id, e.target.value)}
                                            placeholder="Add notes..."
                                            className="w-full mt-2 px-2 py-1.5 text-xs border border-gray-200 rounded-lg resize-none h-14 focus:border-primary focus:ring-1 focus:ring-primary/20 placeholder:text-gray-300 transition-all duration-150"
                                        />
                                    </div>
                                    }
                                </div>
                            );
                        })}
                    </div>
                        </>
                )}
        </>

    );
};

export default ThoughtOrganizer;